import {
  BOGGLE_CHALLENGE_BOARD_DISTRIBUTION_FAMILY_IDS,
  type BoggleChallengeBoardDistributionFamilyId,
} from '~/boggle/config/boardDistributions/types'
import {
  getBoggleChallengeBoardDistributionDefinition,
  getBoggleChallengeBoardDistributionDefinitions,
} from '~/boggle/config/boardDistributions/boardDistributionRegistry'
import type {
  BoggleResolvedChallengeBoardDistribution,
  BoggleResolvedChallengeBonus,
} from '~/boggle/config/challenges/modTypes'
import {
  getBoggleChallengeBonusDefinition,
  getBoggleChallengeBonusDefinitions,
} from '~/boggle/config/bonuses/registry'
import type { BoggleBonusValue, BoggleChallengeBonusId } from '~/boggle/config/bonuses/types'
import {
  getBoggleGameModeDefinition,
  type BoggleGameModeId,
} from '~/boggle/gameModes'
import {
  createSelectedModsBonusResolverContext,
  resolveModularBoggleChallengeBonusFamily,
} from './resolveModFamily'
import { toChallengeRollSeed, resolveBoggleChallengeFamily } from './utils'

export type BoggleChallengeManualSelection = {
  bonusId: BoggleChallengeBonusId
  bonusValue?: BoggleBonusValue
  boardDistributionId: BoggleChallengeBoardDistributionFamilyId | null
}

type BoggleChallengeManualOption<TId extends string> = {
  description: string
  id: TId
  name: string
}

export type BoggleChallengeManualOptions = {
  bonusOptions: readonly BoggleChallengeManualOption<BoggleChallengeBonusId>[]
  boardDistributionOptions: readonly BoggleChallengeManualOption<BoggleChallengeBoardDistributionFamilyId>[]
}

function getManualChallengeBonusDefinitions(gameModeId: BoggleGameModeId) {
  const allowedBonusIds = getBoggleGameModeDefinition(gameModeId).challenge.allowedBonusIds

  return getBoggleChallengeBonusDefinitions(allowedBonusIds)
}

export function getBoggleChallengeManualOptions(
  gameModeId: BoggleGameModeId,
): BoggleChallengeManualOptions {
  return {
    bonusOptions: getManualChallengeBonusDefinitions(gameModeId).map(bonus => ({
      description: bonus.description,
      id: bonus.id,
      name: bonus.name,
    })),
    boardDistributionOptions: getBoggleChallengeBoardDistributionDefinitions(
      BOGGLE_CHALLENGE_BOARD_DISTRIBUTION_FAMILY_IDS,
    ).map(family => ({
      description: family.description,
      id: family.id,
      name: family.name,
    })),
  }
}

export function resolveBoggleManualChallengeEntries(
  gameModeId: BoggleGameModeId,
  selection: BoggleChallengeManualSelection,
  seed: number | string,
) {
  let rngState = toChallengeRollSeed(gameModeId, seed)
  let boardDistribution: BoggleResolvedChallengeBoardDistribution | null = null

  if (selection.boardDistributionId) {
    const family = getBoggleChallengeBoardDistributionDefinition(
      selection.boardDistributionId,
    )
    const resolved = resolveBoggleChallengeFamily(family, rngState, [])

    if (resolved) {
      boardDistribution = resolved.mod as BoggleResolvedChallengeBoardDistribution
      rngState = resolved.rngState
    }
  }

  const bonus = getBoggleChallengeBonusDefinition(selection.bonusId)
  const selectedMods = boardDistribution ? [boardDistribution] : []
  const resolvedBonus = resolveModularBoggleChallengeBonusFamily(
    bonus,
    rngState,
    createSelectedModsBonusResolverContext(selectedMods),
    selection.bonusValue ?? 'double',
  )

  if (!resolvedBonus) {
    throw new Error(`Could not resolve manual challenge bonus for ${selection.bonusId}`)
  }

  return {
    bonuses: [resolvedBonus.mod],
    boardDistribution,
  }
}
